import { useState } from "react";
import { Truck, ExternalLink, Copy, Send, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice, formatApiError } from "../../lib/api";

export const WOLT_AZ = {
  awaiting_dispatch: "Kuryer çağırılmayıb",
  requesting: "Kuryer çağırılır…",
  requested: "Wolt-a göndərildi",
  received: "Wolt qəbul etdi",
  pickup_started: "Kuryer mağazaya gəlir",
  pickup_arrival: "Kuryer mağazanın yanındadır",
  picked_up: "Kuryer götürdü",
  dropoff_started: "Müştəriyə yoldadır",
  dropoff_arrival: "Müştərinin yanındadır",
  delivered: "Çatdırıldı",
  cancelled: "Ləğv edildi",
  rejected: "Wolt rədd etdi",
  no_show: "Müştəriyə çatılmadı",
  failed: "Alınmadı",
  uncertain: "Cavab gəlmədi",
};

// Müştərinin vaxt istəyi (checkout çipləri) — serverdəki `DELIVERY_PREF_AZ` ilə eyni.
export const PREF_AZ = {
  asap: "Hazır olan kimi",
  today: "Bu gün",
  tomorrow: "Sabah",
  evening: "18:00-dan sonra",
  call: "Əvvəlcə zəng edin",
};

const EVENT_AZ = {
  dispatch: "Kuryer çağırıldı",
  cancel: "Ləğv edildi",
  "order.received": "Wolt qəbul etdi",
  "order.pickup_started": "Kuryer mağazaya yola düşdü",
  "order.pickup_arrival": "Kuryer mağazanın yanında",
  "order.picked_up": "Kuryer götürdü",
  "order.dropoff_started": "Müştəriyə yola düşdü",
  "order.dropoff_arrival": "Müştərinin yanında",
  "order.dropoff_completed": "Təhvil verildi",
  "order.delivered": "Çatdırıldı",
  "order.rejected": "Wolt rədd etdi",
  "order.customer_no_show": "Müştəriyə çatılmadı",
  "order.pickup_eta_updated": "Götürmə vaxtı yeniləndi",
  "order.dropoff_eta_updated": "Çatdırılma vaxtı yeniləndi",
};

// Serverdəki WOLT_DISPATCHABLE / WOLT_ACTIVE ilə eyni.
const DISPATCHABLE = new Set(["awaiting_dispatch", "failed", "cancelled", "rejected", "no_show", "uncertain"]);
const CANCELLABLE = new Set(["requested", "received", "pickup_started", "pickup_arrival"]);
const BAD = new Set(["failed", "uncertain", "rejected", "no_show"]);

// Kuryer KOORDİNATA gedir — sahibkar onun haradan gəldiyini bilməlidir.
const COORD_AZ = {
  gps: "📍 Müştərinin telefonundan (GPS)",
  map: "📍 Müştəri xəritədə özü qoyub",
  wolt: "📍 Wolt ünvanı yazıdan tanıyıb",
};

const hhmm = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("az-AZ", { hour: "2-digit", minute: "2-digit" });
};

/** Müştərinin WhatsApp nömrəsi (AdminOrders-dakı köhnə link qutusu ilə eyni qayda). */
const waNumber = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.length === 9) return "994" + digits;
  if (digits.length === 10 && digits.startsWith("0")) return "994" + digits.slice(1);
  return digits;
};

/**
 * Wolt kuryeri — bir sifariş üçün.
 *
 * «Kuryer çağır» İKİ addımlıdır: əvvəl Wolt-un İNDİKİ qiyməti göstərilir
 * (checkout-dakından saatlar keçə bilər), sahibkar müştərinin ödədiyi ilə
 * müqayisə edib təsdiqləyir. Kuryer avtomatik çağırılmır — bağlama hazır
 * olanda (təsisçi qərarı, 2026-10-02).
 */
export const WoltBox = ({ order: o, onChanged }) => {
  const w = o.wolt || {};
  const status = w.status || "awaiting_dispatch";
  const hasAddress = Boolean((o.address?.line || "").trim());
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(!hasAddress);
  const [form, setForm] = useState({ line: o.address?.line || "", flat: o.address?.flat || "",
                                     note: o.address?.note || "" });
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const askPrice = () => run(async () => {
    const { data } = await api.post(`/admin/orders/${o.id}/wolt/preview`);
    if (data.ok) setPreview(data);
    else toast.error(data.message);
  });

  const dispatch = () => run(async () => {
    const { data } = await api.post(`/admin/orders/${o.id}/wolt/dispatch`);
    setPreview(null);
    if (data.ok) toast.success("Kuryer çağırıldı");
    else toast.error(data.message);
    await onChanged();
  });

  const cancel = () => run(async () => {
    const { data } = await api.post(`/admin/orders/${o.id}/wolt/cancel`, { reason });
    if (data.ok) {
      toast.success("Wolt çatdırılması ləğv edildi");
      setCancelOpen(false);
      setReason("");
    } else {
      toast.error(data.message);
    }
    await onChanged();
  });

  const saveAddress = () => run(async () => {
    await api.patch(`/admin/orders/${o.id}/address`,
                    { line: form.line, flat: form.flat, note: form.note, city: "Bakı" });
    toast.success("Ünvan yeniləndi");
    setEditing(false);
    setPreview(null);
    await onChanged();
  });

  const copyTrack = async () => {
    try {
      await navigator.clipboard.writeText(w.tracking_url);
      toast.success("Link kopyalandı");
    } catch {
      window.prompt("Linki kopyalayın:", w.tracking_url);
    }
  };

  // İzləmə linkini müştəriyə Wolt özü SMS ilə göndərir (kuryer çağırılanda və
  // götürəndə). Bu düymə EHTİYAT yoldur: müştəri «SMS gəlmədi» deyəndə.
  // Hədiyyədə link ALICIYA gedir — kuryer də ona zəng edir.
  const gift = Boolean(o.recipient?.phone);
  const sendTrack = () => {
    const text = encodeURIComponent(gift
      ? `Salam! Sizə Sparkle-dan hədiyyə yoldadır. Kuryeri buradan izləyə bilərsiniz: ${w.tracking_url}`
      : `Salam! Sparkle — ${o.order_number} nömrəli sifarişiniz yoldadır. ` +
        `Kuryeri buradan izləyə bilərsiniz: ${w.tracking_url}`);
    const phone = gift ? o.recipient.phone : o.customer?.phone;
    window.open(`https://wa.me/${waNumber(phone)}?text=${text}`, "_blank", "noopener");
  };

  const feePaid = o.delivery_fee || (o.delivery?.status === "paid" ? o.delivery.paid_amount || o.delivery.amount : 0);
  const events = (w.events || []).filter((e) => !e.stale).slice(-6).reverse();

  return (
    <div className="mt-5 border border-brand-line rounded-md p-4 bg-white" data-testid={`wolt-box-${o.order_number}`}>
      <div className="flex items-center gap-2 mb-3">
        <Truck size={16} strokeWidth={1.5} />
        <span className="overline">Wolt kuryeri</span>
        <span
          className={`ml-auto text-xs ${BAD.has(status) ? "text-destructive font-medium" : status === "delivered" ? "text-brand-oxid" : "text-brand-sub"}`}
          data-testid={`wolt-status-${o.order_number}`}
        >
          {WOLT_AZ[status] || status}
        </span>
      </div>

      {/* Ünvan — kuryer çağırılmayıbsa düzəldilə bilər (Wolt-da sifariş
          sonradan dəyişdirilə bilmir, yalnız ləğv edib yenidən yaratmaq olar). */}
      {editing ? (
        <div className="space-y-2 mb-3">
          <input
            value={form.line}
            onChange={(e) => setForm({ ...form, line: e.target.value })}
            placeholder="Küçə və ev nömrəsi"
            data-testid={`wolt-addr-line-${o.order_number}`}
            className="w-full border border-brand-line rounded-md px-3 py-2.5 text-sm outline-none focus:border-brand-ink"
          />
          <input
            value={form.flat}
            onChange={(e) => setForm({ ...form, flat: e.target.value })}
            placeholder="Mənzil, mərtəbə, giriş, domofon"
            data-testid={`wolt-addr-flat-${o.order_number}`}
            className="w-full border border-brand-line rounded-md px-3 py-2.5 text-sm outline-none focus:border-brand-ink"
          />
          <input
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Kuryerə qeyd"
            className="w-full border border-brand-line rounded-md px-3 py-2.5 text-sm outline-none focus:border-brand-ink"
          />
          <div className="flex gap-2">
            <button type="button" onClick={saveAddress} disabled={busy || form.line.trim().length < 3}
              data-testid={`wolt-addr-save-${o.order_number}`}
              className="bg-brand-ink text-white px-4 py-2 text-sm rounded-md disabled:opacity-50">
              Ünvanı saxla
            </button>
            {hasAddress && (
              <button type="button" onClick={() => setEditing(false)}
                className="border border-brand-line px-4 py-2 text-sm rounded-md">
                İmtina
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2 mb-3">
          <div className="text-brand-sub flex-1 space-y-0.5">
            <p>
              {[o.address?.city, o.address?.line].filter(Boolean).join(", ")}
              {o.address?.flat ? ` · ${o.address.flat}` : ""}
            </p>
            {o.address?.note && (
              <p className="text-xs" data-testid={`wolt-note-${o.order_number}`}>📝 Kuryerə: {o.address.note}</p>
            )}
          </div>
          {DISPATCHABLE.has(status) && (
            <button type="button" onClick={() => setEditing(true)} title="Ünvanı düzəlt"
              data-testid={`wolt-addr-edit-${o.order_number}`}
              className="text-brand-muted hover:text-brand-ink shrink-0">
              <Pencil size={14} strokeWidth={1.5} />
            </button>
          )}
        </div>
      )}

      {!editing && o.address?.lat != null && o.address?.lon != null && (
        <p className="text-xs text-brand-muted mb-1" data-testid={`wolt-coords-${o.order_number}`}>
          {COORD_AZ[o.address.coord_source] || COORD_AZ.wolt}
          {o.address.pin_label && (o.address.coord_source === "gps" || o.address.coord_source === "map")
            ? ` · ${o.address.pin_label} yaxınlığı` : ""} ·{" "}
          <a
            href={`https://www.openstreetmap.org/?mlat=${o.address.lat}&mlon=${o.address.lon}#map=18/${o.address.lat}/${o.address.lon}`}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-brand-ink"
          >
            xəritədə bax
          </a>
        </p>
      )}

      {/* Kuryer kimə zəng edəcək və nə vaxt istənilir — düymədən ƏVVƏL görünür. */}
      {gift && (
        <p className="text-xs text-brand-sub mb-1" data-testid={`wolt-recipient-${o.order_number}`}>
          🎁 Alıcı: {o.recipient.name} · {o.recipient.phone}
          {o.recipient.sms === false ? " · SMS göndərilməyəcək (sürpriz)" : ""}
        </p>
      )}
      {o.delivery_pref && o.delivery_pref !== "asap" && (
        <p className="text-xs text-brand-sub mb-1" data-testid={`wolt-pref-${o.order_number}`}>
          ⏰ Müştərinin istəyi: {PREF_AZ[o.delivery_pref] || o.delivery_pref}
        </p>
      )}

      <p className="text-xs text-brand-muted mb-3">
        {feePaid ? `Müştəri çatdırılma üçün ödəyib: ${formatPrice(feePaid)}` : "Müştəri çatdırılma üçün ödəməyib"}
        {w.price ? ` · Wolt qiyməti: ${formatPrice(w.price)}` : ""}
      </p>

      {w.error && BAD.has(status) && (
        <p className="flex items-start gap-2 text-sm text-destructive mb-3">
          <AlertTriangle size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" /> {w.error}
        </p>
      )}

      {o.seq && (
        <p className="text-xs text-brand-sub mb-3" data-testid={`wolt-seq-${o.order_number}`}>
          📦 Qutunun üstünə yazın: <b>№ {o.seq}</b> — kuryer bağlamanı bu nömrə ilə tanıyır.
        </p>
      )}

      {/* Kuryer çağırma — iki addım: qiymət → təsdiq. */}
      {DISPATCHABLE.has(status) && hasAddress && !editing && !preview && (
        <button type="button" onClick={askPrice} disabled={busy}
          data-testid={`wolt-call-${o.order_number}`}
          className="bg-brand-ink text-white px-4 py-2.5 text-sm rounded-md disabled:opacity-50">
          {busy ? "Wolt soruşulur…" : status === "awaiting_dispatch" ? "Kuryer çağır" : "Yenidən kuryer çağır"}
        </button>
      )}

      {preview && (
        <div className="border border-brand-line rounded-md p-3 bg-brand-bg2/50 space-y-2" data-testid={`wolt-preview-${o.order_number}`}>
          <p className="text-sm">
            Wolt indi: <span className="font-medium">{formatPrice(preview.price)}</span>
            {preview.pickup_eta_minutes != null && ` · götürmə ~${preview.pickup_eta_minutes} dəq`}
            {preview.dropoff_eta_minutes != null && ` · çatdırılma ~${preview.dropoff_eta_minutes} dəq`}
          </p>
          {preview.formatted_address && (
            <p className="text-xs text-brand-muted">Wolt ünvanı belə tanıdı: {preview.formatted_address}</p>
          )}
          {preview.parcel && (
            <p className="text-xs text-brand-muted" data-testid={`wolt-parcel-${o.order_number}`}>
              📦 Bağlama: ~{preview.parcel.weight_gram} q · {preview.parcel.width_cm}×{preview.parcel.height_cm}×{preview.parcel.depth_cm} sm
              — Wolt kuryer növünü buna görə seçir.
            </p>
          )}
          {preview.fee_paid > 0 && preview.price > preview.fee_paid && (
            <p className="text-xs text-destructive">
              Wolt qiyməti müştərinin ödədiyindən {formatPrice(preview.price - preview.fee_paid)} çoxdur — fərqi mağaza ödəyəcək.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={dispatch} disabled={busy}
              data-testid={`wolt-confirm-${o.order_number}`}
              className="bg-brand-ink text-white px-4 py-2.5 text-sm rounded-md disabled:opacity-50">
              {busy ? "Çağırılır…" : "Təsdiqlə — kuryeri çağır"}
            </button>
            <button type="button" onClick={() => setPreview(null)} disabled={busy}
              className="border border-brand-line px-4 py-2.5 text-sm rounded-md">
              İmtina
            </button>
          </div>
        </div>
      )}

      {/* Kuryer yoldadır: izləmə linki və təxmini vaxtlar. */}
      {w.tracking_url && !DISPATCHABLE.has(status) && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <a href={w.tracking_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 border border-brand-line px-3 py-2 text-sm rounded-md hover:bg-brand-bg2">
              <ExternalLink size={14} strokeWidth={1.5} /> İzlə
            </a>
            <button type="button" onClick={copyTrack}
              className="flex items-center gap-2 border border-brand-line px-3 py-2 text-sm rounded-md hover:bg-brand-bg2">
              <Copy size={14} strokeWidth={1.5} /> Linki kopyala
            </button>
            <button type="button" onClick={sendTrack} data-testid={`wolt-wa-${o.order_number}`}
              className="flex items-center gap-2 border border-brand-line px-3 py-2 text-sm rounded-md hover:bg-brand-bg2">
              <Send size={14} strokeWidth={1.5} /> Müştəriyə WhatsApp-da göndər
            </button>
          </div>
          {(w.pickup_eta || w.dropoff_eta) && status !== "delivered" && (
            <p className="text-xs text-brand-muted">
              {w.pickup_eta && `Götürmə ~${hhmm(w.pickup_eta)}`}
              {w.pickup_eta && w.dropoff_eta && " · "}
              {w.dropoff_eta && `Çatdırılma ~${hhmm(w.dropoff_eta)}`}
            </p>
          )}
        </div>
      )}

      {CANCELLABLE.has(status) && (
        cancelOpen ? (
          <div className="mt-3 space-y-2">
            <input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Səbəb (Wolt-da görünəcək)"
              className="w-full border border-brand-line rounded-md px-3 py-2.5 text-sm outline-none focus:border-brand-ink" />
            <div className="flex gap-2">
              <button type="button" onClick={cancel} disabled={busy}
                data-testid={`wolt-cancel-confirm-${o.order_number}`}
                className="border border-destructive text-destructive px-4 py-2 text-sm rounded-md hover:bg-destructive hover:text-white disabled:opacity-50">
                Wolt çatdırılmasını ləğv et
              </button>
              <button type="button" onClick={() => setCancelOpen(false)}
                className="border border-brand-line px-4 py-2 text-sm rounded-md">
                Geri
              </button>
            </div>
            <p className="text-xs text-brand-muted">
              Kuryer götürməyə yola düşəndən sonra ləğv yalnız Wolt dəstəyi ilə mümkündür.
            </p>
          </div>
        ) : (
          <button type="button" onClick={() => setCancelOpen(true)}
            data-testid={`wolt-cancel-${o.order_number}`}
            className="mt-3 text-xs text-brand-muted underline underline-offset-4 hover:text-destructive">
            Wolt çatdırılmasını ləğv et
          </button>
        )
      )}

      {events.length > 0 && (
        <div className="mt-4 border-t border-brand-line pt-3 space-y-1">
          {events.map((e) => (
            <p key={e.id} className="text-xs text-brand-muted">
              <span className="font-mono">{hhmm(e.at || e.received_at)}</span> · {EVENT_AZ[e.type] || e.type}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
